import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { cn } from '../utils';

/**
 * Brand mark: lime circle with a tilted dumbbell + two sparkles.
 * The dumbbell is the gym/training visual cue; the sparkles add the
 * "performance shine" — together they encode the "Performance Club
 * OS" tagline in a single 36px mark. Drawn as inline SVG so it
 * inherits the surrounding text color and scales cleanly.
 */
const BrandLogo = ({ size = 36 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="shrink-0"
  >
    <circle cx="18" cy="18" r="17" fill="#c1f148" />
    {/* Sparkle, top-left */}
    <path
      d="M8.5 9.2 L9.1 11.2 L11.1 11.8 L9.1 12.4 L8.5 14.4 L7.9 12.4 L5.9 11.8 L7.9 11.2 Z"
      fill="#0a1208"
    />
    {/* Sparkle, bottom-right */}
    <path
      d="M27.5 21.8 L28.1 23.8 L30.1 24.4 L28.1 25 L27.5 27 L26.9 25 L24.9 24.4 L26.9 23.8 Z"
      fill="#0a1208"
    />
    {/* Dumbbell, tilted */}
    <g transform="rotate(-30 18 18)">
      {/* Bar */}
      <rect x="8" y="16.7" width="20" height="2.6" rx="1" fill="#0a1208" />
      {/* Left weight plate */}
      <rect x="5.5" y="13.2" width="4" height="9.6" rx="1.2" fill="#0a1208" />
      <rect x="6.8" y="14.6" width="1.4" height="6.8" rx="0.5" fill="#c1f148" />
      {/* Right weight plate */}
      <rect x="26.5" y="13.2" width="4" height="9.6" rx="1.2" fill="#0a1208" />
      <rect x="27.8" y="14.6" width="1.4" height="6.8" rx="0.5" fill="#c1f148" />
    </g>
  </svg>
);

const InlineBrandMark = ({ compact, subtitle }) => (
  <div className="flex min-w-0 items-center gap-3">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center">
      <BrandLogo size={36} />
    </span>
    {!compact && (
      <div className="min-w-0">
        <p className="truncate font-display text-base uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
        {subtitle && <p className="truncate text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">{subtitle}</p>}
      </div>
    )}
  </div>
);

export const PublicLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/features', label: t('landing.nav.features') },
    { to: '/free', label: t('landing.nav.free') },
    { to: '/about', label: t('landing.nav.about') },
    { to: '/contact', label: t('landing.nav.contact') },
  ];

  return (
    <div className="relative min-h-screen min-w-0 max-w-[100vw] overflow-x-clip text-[var(--color-text)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-10%] top-16 h-80 w-80 rounded-full bg-primary-300/10 blur-3xl" />
        <div className="absolute right-[-8%] top-28 h-96 w-96 rounded-full bg-accent-400/12 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </div>

      <header className="sticky top-0 z-50 border-b border-[var(--color-border)] bg-[rgba(6,12,10,0.62)] backdrop-blur-2xl">
        <div className="layout-shell py-4">
          <div className="flex min-w-0 flex-wrap items-center justify-start gap-3 sm:flex-nowrap sm:gap-4 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <Link to="/" className="min-w-0 shrink overflow-hidden lg:max-w-[17rem] xl:max-w-[20rem]">
                <div className="sm:hidden">
                  <InlineBrandMark compact />
                </div>
                <div className="hidden sm:block">
                  <InlineBrandMark subtitle={t('landing.brandSubtitle')} />
                </div>
              </Link>

              <nav className="hidden min-w-0 items-center gap-1 md:flex lg:gap-2">
                {navLinks.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={cn(
                      'rounded-full px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap transition-all lg:px-4 lg:text-[11px] lg:tracking-[0.18em]',
                      location.pathname === to
                        ? 'bg-white/10 text-[var(--color-text)]'
                        : 'text-[var(--color-text-muted)] hover:bg-white/5 hover:text-[var(--color-text)]',
                    )}
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="hidden min-w-0 shrink-0 items-center gap-2 md:flex lg:-translate-x-2 lg:justify-self-end">
              <LanguageSwitcher variant="buttons" />
              <Link to="/login" className="whitespace-nowrap rounded-full border border-transparent px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:border-[var(--color-border)] hover:text-[var(--color-text)]">
                {t('landing.nav.login')}
              </Link>
              <Link to="/register" className="btn btn-primary whitespace-nowrap">
                {t('landing.nav.getStarted')}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="ml-auto flex w-[8.75rem] shrink-0 items-center justify-end gap-2 md:hidden">
              <LanguageSwitcher variant="buttons" className="min-w-0 max-w-full" />
              <button
                type="button"
                onClick={() => setMobileMenuOpen((value) => !value)}
                className="rounded-full border border-[var(--color-border)] p-2 text-[var(--color-text-muted)]"
                aria-label={t('common.toggleMenu')}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="layout-shell border-t border-[var(--color-border)] py-4 md:hidden">
            <div className="flex flex-col gap-2">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-sm font-medium text-[var(--color-text)]"
                >
                  {label}
                </Link>
              ))}
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="rounded-2xl border border-[var(--color-border)] px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                {t('landing.nav.login')}
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn btn-primary mt-2">
                {t('landing.nav.getStarted')}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="border-t border-[var(--color-border)] bg-[rgba(6,12,10,0.62)] backdrop-blur-2xl">
        <div className="layout-shell grid gap-8 py-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <InlineBrandMark subtitle={t('landing.footer.subtitle')} />
            <p className="mt-4 max-w-xl text-sm text-[var(--color-text-muted)]">
              {t('landing.footer.description')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                {label}
              </Link>
            ))}
            <Link to="/login" className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              {t('landing.nav.login')}
            </Link>
            <Link to="/register" className="rounded-2xl border border-primary-200/20 bg-primary-200/10 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
              {t('landing.nav.getStarted')}
            </Link>
            <div className="col-span-2 mt-2 sm:col-span-4 lg:col-span-2">
              <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">{t('landing.footer.legalHeading')}</p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Link to="/legal/terms" className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  {t('landing.footer.legalTerms')}
                </Link>
                <Link to="/legal/privacy" className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  {t('landing.footer.legalPrivacy')}
                </Link>
                <Link to="/legal/cookies" className="rounded-2xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  {t('landing.footer.legalCookies')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
